import { resolveFloodSolverKind, resolveFloodAnalysisEnabled } from '@/utils/map3d/threejs-flood/flood-solver-factory'

// Single source of environment configuration. Nothing outside this file may
// read import.meta.env — add a key here and import `config` instead.
//
// Each key resolves runtime env (window.__ENV__ from /env.js) → build-time
// env (import.meta.env) → default. Runtime values come from container env
// vars in Docker (see docker/40-env-js.sh), so deployments can reconfigure
// without rebuilding the image. Missing/empty runtime keys fall back.
//
// Convention: config key <serviceName>Url ↔ env var VITE_APP_<SERVICE_NAME>_URL.
const rt: Record<string, string | undefined> =
  typeof window !== 'undefined' ? window.__ENV__ ?? {} : {}

// Single origin for the external Aetos platform app ("Aetos Browser").
// Geocode, cube, and workflow tile URLs derive from it by path; their
// per-service vars override the derivation where an environment diverges.
const aetosAppUrl =
  rt.VITE_APP_AETOS_APP_URL || import.meta.env.VITE_APP_AETOS_APP_URL || 'https://app.aetosky.com'

const config = {
  aetosAppUrl,
  appName: rt.VITE_APP_NAME || import.meta.env.VITE_APP_NAME || 'Aetos Terra',
  appLogo: rt.VITE_APP_LOGO || import.meta.env.VITE_APP_LOGO || '',
  appFavicon: rt.VITE_APP_FAVICON || import.meta.env.VITE_APP_FAVICON || '/favicon.ico',
  accessTokenMap3d: 
    rt.VITE_APP_ACCESS_TOKEN_MAP_3D || import.meta.env.VITE_APP_ACCESS_TOKEN_MAP_3D,
  terrainSources:
    rt.VITE_APP_TERRAIN_SOURCES || import.meta.env.VITE_APP_TERRAIN_SOURCES || '',
  // Extra XYZ basemaps, `Name=url;Name2=url`.
  basemapSources:
    rt.VITE_APP_BASEMAP_SOURCES || import.meta.env.VITE_APP_BASEMAP_SOURCES || '',
  // Base url
  baseUrl: import.meta.env.VITE_APP_BASE_URL || '',
  // Backend url
  backendUrl: rt.VITE_APP_BACKEND_URL || import.meta.env.VITE_APP_BACKEND_URL || '',
  // Auth urls
  oidcAuthorityUrl: 
    rt.VITE_APP_OIDC_AUTHORITY || import.meta.env.VITE_APP_OIDC_AUTHORITY || 'https://accounts.aetosky.com/realms/aetosky',
  oidcClientId:
    rt.VITE_APP_OIDC_CLIENT_ID || import.meta.env.VITE_APP_OIDC_CLIENT_ID || 'terra-frontend',
  // External service urls
  chatbotUrl: 
    rt.VITE_APP_CHATBOT_URL || import.meta.env.VITE_APP_CHATBOT_URL || '',
  geocodeUrl:
    rt.VITE_APP_GEOCODE_URL || import.meta.env.VITE_APP_GEOCODE_URL || `${aetosAppUrl}/api/services/geocode`,
  cubeUrl:
    rt.VITE_APP_CUBE_URL || import.meta.env.VITE_APP_CUBE_URL || `${aetosAppUrl}/aetos-cube/api/v1`,
  workflowTileUrl:
    rt.VITE_APP_WORKFLOW_TILE_URL || import.meta.env.VITE_APP_WORKFLOW_TILE_URL || `${aetosAppUrl}/core-workflow-tile`,
  // Microservice urls
  terrainTileUrl:
    rt.VITE_APP_TERRAIN_TILE_URL || import.meta.env.VITE_APP_TERRAIN_TILE_URL || '/terrain-tile',
  terrainPathUrl:
    rt.VITE_APP_TERRAIN_PATH_URL || import.meta.env.VITE_APP_TERRAIN_PATH_URL || '/terrain-tile',
  rasterTileUrl:
    rt.VITE_APP_RASTER_TILE_URL || import.meta.env.VITE_APP_RASTER_TILE_URL || '/raster-tile',
  vectorTileUrl:
    rt.VITE_APP_VECTOR_TILE_URL || import.meta.env.VITE_APP_VECTOR_TILE_URL || '/vector-tile',
  poiTileUrl: 
    rt.VITE_APP_POI_TILE_URL || import.meta.env.VITE_APP_POI_TILE_URL || '/poi-tile',
  // Satellite basemap XYZ template; {s} expands to subdomains 0-3 where supported.
  satelliteTileUrl:
    rt.VITE_APP_SATELLITE_TILE_URL || import.meta.env.VITE_APP_SATELLITE_TILE_URL || 'https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
  routeTileUrl:
    rt.VITE_APP_ROUTE_TILE_URL || import.meta.env.VITE_APP_ROUTE_TILE_URL || '/route-tile',
  adminBoundaryUrl:
    rt.VITE_APP_ADMIN_BOUNDARY_URL ||
    import.meta.env.VITE_APP_ADMIN_BOUNDARY_URL ||
    '/admin-boundary',
  companionUrl:
    rt.VITE_APP_COMPANION_URL || import.meta.env.VITE_APP_COMPANION_URL || '/companion',
  // Dev-only escape hatch for the Keycloak gate on /dev/* diagnostic routes
  // (e.g. running WebGPU probes headless via Playwright, no SSO session
  // available). `import.meta.env.DEV` is statically false in a production
  // build, so Vite dead-code-eliminates this — it can never ship. No
  // window.__ENV__ fallback: this has no meaning outside local `vite dev`.
  devSkipAuthForDevRoutes: import.meta.env.DEV && import.meta.env.VITE_DEV_SKIP_AUTH === 'true',
  // `pnpm dev` connects to the task-update websocket by default, which
  // requires a running backend socket server; local frontend-only work
  // doesn't need it and it just spams reconnect attempts/console noise
  // when the backend isn't up. `import.meta.env.DEV` is statically false in
  // a production build, so Vite dead-code-eliminates this branch.
  websocketEnabled: !import.meta.env.DEV || import.meta.env.VITE_DEV_WEBSOCKET === 'true',
  // V2-F: production flood solver selection. Unset -> 'v2' (current
  // default); an unrecognized override falls back to 'v1' (the rollback
  // side), never silently to the default -- see resolveFloodSolverKind's
  // own doc comment for why. Runtime-overridable so ops can roll back
  // without a rebuild, same as every other Docker-injected setting here.
  floodSolverKind: resolveFloodSolverKind(rt.VITE_APP_FLOOD_SOLVER || import.meta.env.VITE_APP_FLOOD_SOLVER),
  // Gaussian splat (GPU) option on aerial tasks. Strict: only 'true' enables;
  // unset, empty or any other value is off.
  gaussianSplatEnabled:
    (rt.VITE_APP_GAUSSIAN_SPLAT || import.meta.env.VITE_APP_GAUSSIAN_SPLAT) === 'true',
  // Phase-4 analysis kill switch. Unset -> enabled (production default); an
  // unrecognized override falls back to DISABLED, not the default -- the
  // opposite direction from floodSolverKind above. See
  // resolveFloodAnalysisEnabled's own doc comment for why.
  floodAnalysisEnabled: resolveFloodAnalysisEnabled(rt.VITE_APP_FLOOD_ANALYSIS || import.meta.env.VITE_APP_FLOOD_ANALYSIS),
}

export default config
